import { useMemo } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LiveTile } from "../components/Lubrication/LiveTile";
import { CsvUpload } from "../components/Lubrication/CsvUpload";
import { VolumeChart } from "../components/Lubrication/VolumeChart";
import { StaticChainageChart } from "../components/Lubrication/StaticChainageChart";
import { PerBoxBarChart } from "../components/Lubrication/PerBoxBarChart";
import { BoxSelectionScatter } from "../components/Lubrication/BoxSelectionScatter";
import { OvercutChart } from "../components/Lubrication/OvercutChart";
import { usePersistentState } from "../lib/usePersistentState";

type Range = "24h" | "7d" | "drive";
type PlotKey =
  | "volume"
  | "staticChainage"
  | "perBox"
  | "boxSelection"
  | "overcut";

const RANGES: { id: Range; label: string }[] = [
  { id: "24h",   label: "24h" },
  { id: "7d",    label: "7d" },
  { id: "drive", label: "Drive" },
];

const PLOTS: { id: PlotKey; label: string }[] = [
  { id: "volume",         label: "Volume by chainage / time" },
  { id: "staticChainage", label: "Total delivery at chainage" },
  { id: "perBox",         label: "Per-box bars" },
  { id: "boxSelection",   label: "Box selection over time" },
  { id: "overcut",        label: "Overcut" },
];

export function Lubrication() {
  const [range, setRange] = usePersistentState<Range>("lubrication.range", "drive");
  const [plot, setPlot] = usePersistentState<PlotKey>("lubrication.plot", "volume");
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });

  const { fromIso, toIso } = useMemo(() => {
    const to = new Date();
    const driveStart = drive.data?.startDate ? new Date(drive.data.startDate) : null;
    let from: Date;
    if (range === "drive") {
      from = driveStart ?? new Date(Date.now() - 7 * 24 * 3600 * 1000);
    } else if (range === "24h") {
      from = new Date(Date.now() - 24 * 3600 * 1000);
    } else {
      from = new Date(Date.now() - 7 * 24 * 3600 * 1000);
    }
    if (driveStart && from < driveStart) from = driveStart;
    return { fromIso: from.toISOString(), toIso: to.toISOString() };
  }, [range, drive.data?.startDate]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between pt-1">
        <div>
          <h2 className="text-[13px] font-semibold tracking-[0.18em] uppercase text-text">
            Bentonite lubrication
          </h2>
          <div className="text-[11px] text-muted mt-1">
            Where the DAQ doesn&rsquo;t sync bentonite via the API, upload manual{" "}
            <code>Bentonite N.csv</code> exports.
          </div>
        </div>
      </div>

      <CsvUpload />

      <LiveTile />

      <div className="flex flex-wrap items-center gap-3">
        <PlotSelect value={plot} onChange={setPlot} options={PLOTS} />
        <div className="flex-1" />
        <RangeToggle range={range} onChange={setRange} />
      </div>

      {plot === "volume"         && <VolumeChart         fromIso={fromIso} toIso={toIso} />}
      {plot === "staticChainage" && <StaticChainageChart fromIso={fromIso} toIso={toIso} />}
      {plot === "perBox"         && <PerBoxBarChart      fromIso={fromIso} toIso={toIso} />}
      {plot === "boxSelection"   && <BoxSelectionScatter fromIso={fromIso} toIso={toIso} />}
      {plot === "overcut"        && <OvercutChart        fromIso={fromIso} toIso={toIso} />}
    </div>
  );
}

function RangeToggle({ range, onChange }: { range: Range; onChange: (r: Range) => void }) {
  return (
    <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden">
      {RANGES.map((r) => (
        <button
          key={r.id}
          onClick={() => onChange(r.id)}
          className={clsx(
            "px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors",
            range === r.id ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function PlotSelect<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="bg-surface2 border border-border1 rounded px-3 py-1.5 text-[12px] text-text outline-none focus:border-wb/60"
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>{o.label}</option>
      ))}
    </select>
  );
}
